import React, { createContext, use, useState, useTransition, useOptimistic, useDeferredValue } from 'react';
import { TOAST } from '@buzunarul/shared-constants';
import { classifyError, errorRecoveryManager } from '../../../utils/errorRecovery';
import type { 
  ToastData, 
  ToastContextValue, 
  ToastProviderProps, 
  ToastPosition,
  ToastVariant,
  UseToastReturn
} from './types';

/**
 * 🍞 REACT 19 ENHANCED TOAST SYSTEM
 * 
 * Enhanced toast system with React 19 concurrent features:
 * - useOptimistic for instant toast updates
 * - useTransition for non-blocking operations
 * - useDeferredValue for performance optimization
 * - Enhanced error handling with classification
 * - Auto-recovery for failed operations
 */

// Context pentru toast system
const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Generator de ID-uri unice pentru toast-uri
let toastIdCounter = 0;
const generateToastId = () => `toast-${++toastIdCounter}-${Date.now()}`;

/**
 * 🚀 React 19 Enhanced Toast Provider with Concurrent Features
 */
export function ToastProvider({ 
  children, 
  position = 'top-right',
  maxToasts = 5 
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [currentPosition, setCurrentPosition] = useState<ToastPosition>(position);
  
  // 🚀 React 19: Concurrent features
  const [isPending, startTransition] = useTransition();
  const [optimisticToasts, setOptimisticToasts] = useOptimistic(
    toasts,
    (currentToasts, optimisticToast: ToastData) => {
      // Add optimistic toast immediately
      const updated = [optimisticToast, ...currentToasts];
      return updated.slice(0, maxToasts);
    }
  );
  
  // 🚀 React 19: Deferred value for performance
  const deferredToasts = useDeferredValue(optimisticToasts);

  // 🚀 React 19: Enhanced toast addition with optimistic updates
  const addToast = (toastData: Omit<ToastData, 'id'>) => {
    const id = generateToastId();
    const newToast: ToastData = {
      ...toastData,
      id,
      variant: toastData.variant || 'info',
      duration: toastData.duration ?? TOAST.DURATION.MEDIUM
    };

    // 🚀 React 19: Optimistic update for instant feedback
    setOptimisticToasts(newToast);

    // Non-blocking update with transition
    startTransition(() => {
      setToasts(prev => {
        const updated = [newToast, ...prev];
        return updated.slice(0, maxToasts);
      });
    });

    // Auto-dismiss dacă nu este persistent
    if (!newToast.persistent && newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, newToast.duration);
    }

    return id;
  };

  // 🚀 React 19: Enhanced dismiss with transition
  const dismissToast = (id: string) => {
    startTransition(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    });
  };

  // 🚀 React 19: Enhanced dismiss all with transition
  const dismissAll = () => {
    startTransition(() => {
      setToasts([]);
    });
  };

  // 🚀 React 19: Enhanced error handling for toast operations
  const handleToastError = (error: Error, message: string) => {
    const classifiedError = classifyError(error);
    
    // Use React 19's reportError API if available
    if (typeof window !== 'undefined' && 'reportError' in window) {
      window.reportError(classifiedError);
    }
    
    // Show error toast with recovery option
    const errorToast: ToastData = {
      id: generateToastId(),
      message: `${message}: ${error.message}`,
      variant: 'error',
      duration: TOAST.DURATION.LONG,
      persistent: classifiedError.isRecoverable,
      action: classifiedError.isRecoverable ? {
        label: 'Încearcă din nou',
        onClick: async () => {
          try {
            await errorRecoveryManager.attemptRecovery(classifiedError);
            toastHelpers.success('Operațiunea a fost recuperată cu succes');
          } catch (recoveryError) {
            toastHelpers.error('Recuperarea a eșuat');
          }
        }
      } : undefined
    };
    
    setOptimisticToasts(errorToast);
    startTransition(() => {
      setToasts(prev => [errorToast, ...prev].slice(0, maxToasts));
    });
  };

  // 🚀 React 19: Enhanced toast helpers with error handling
  const toastHelpers = {
    info: (message: string, options?: Partial<ToastData>) => 
      addToast({ ...options, message, variant: 'info' }),
    
    success: (message: string, options?: Partial<ToastData>) => 
      addToast({ ...options, message, variant: 'success' }),
    
    warning: (message: string, options?: Partial<ToastData>) => 
      addToast({ ...options, message, variant: 'warning' }),
    
    error: (message: string, options?: Partial<ToastData>) => 
      addToast({ ...options, message, variant: 'error' }),
    
    custom: (data: Omit<ToastData, 'id'>) => addToast(data),
    
    // 🚀 React 19: New error handling helper
    handleError: (error: Error, context?: string) => {
      const message = context || 'A apărut o eroare';
      handleToastError(error, message);
    },
    
    // 🚀 React 19: New loading helper with optimistic updates
    loading: (message: string, options?: Partial<ToastData>) => {
      const loadingToast: ToastData = {
        ...options,
        message,
        variant: 'info',
        persistent: true, // Loading toasts should be persistent
        duration: undefined
      };
      return addToast(loadingToast);
    },
    
    // 🚀 React 19: New update helper for existing toasts
    update: (id: string, updates: Partial<ToastData>) => {
      startTransition(() => {
        setToasts(prev => 
          prev.map(toast => 
            toast.id === id 
              ? { ...toast, ...updates }
              : toast
          )
        );
      });
    }
  };

  // 🚀 React 19: Enhanced context value with concurrent features
  const contextValue: ToastContextValue = {
    toasts: deferredToasts, // Use deferred value for performance
    toast: toastHelpers,
    dismiss: dismissToast,
    dismissAll,
    position: currentPosition,
    setPosition: setCurrentPosition,
    // 🚀 React 19: New concurrent features
    isPending,
    optimisticToasts,
    handleError: handleToastError
  };

  return (
    <ToastContext value={contextValue}>
      {children}
    </ToastContext>
  );
}

/**
 * 🚀 React 19 Enhanced Hook for Toast System
 * Includes concurrent features and enhanced error handling
 */
export function useToast(): UseToastReturn {
  const context = use(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  // 🚀 React 19: Enhanced return with concurrent features
  return {
    toasts: context.toasts,
    toast: context.toast,
    dismiss: context.dismiss,
    dismissAll: context.dismissAll,
    // 🚀 React 19: New concurrent features
    isPending: context.isPending,
    optimisticToasts: context.optimisticToasts,
    handleError: context.handleError
  };
}

/**
 * 🚀 React 19 Enhanced Hook for Toast Position Management
 */
export function useToastPosition() {
  const context = use(ToastContext);
  
  if (!context) {
    throw new Error('useToastPosition must be used within a ToastProvider');
  }

  return {
    position: context.position,
    setPosition: context.setPosition,
    // 🚀 React 19: Include pending state for position changes
    isPending: context.isPending
  };
}

/**
 * 🚀 React 19 New Hook for Loading States with Toast Integration
 * Provides optimistic loading states with toast feedback
 */
export function useToastLoading() {
  const { toast } = useToast();
  
  const showLoading = (message: string, options?: Partial<ToastData>) => {
    return toast.loading(message, options);
  };
  
  const updateLoading = (id: string, message: string, variant: ToastVariant = 'info') => {
    toast.update(id, { message, variant });
  };
  
  const finishLoading = (id: string, message: string, variant: ToastVariant = 'success') => {
    toast.update(id, { 
      message, 
      variant, 
      persistent: false, 
      duration: TOAST.DURATION.MEDIUM 
    });
  };
  
  return {
    showLoading,
    updateLoading,
    finishLoading
  };
}

/**
 * 🚀 React 19 New Hook for Error Handling with Toast Integration
 * Provides enhanced error handling with recovery options
 */
export function useToastError() {
  const { toast } = useToast();
  
  const handleError = (error: Error, context?: string) => {
    toast.handleError(error, context);
  };
  
  const handleAsyncError = async (asyncFn: () => Promise<any>, context?: string) => {
    try {
      return await asyncFn();
    } catch (error) {
      handleError(error as Error, context);
      throw error; // Re-throw for further handling if needed
    }
  };
  
  return {
    handleError,
    handleAsyncError
  };
}

/**
 * 🚀 React 19 New Hook for Optimistic Toast Operations
 * Provides optimistic updates for better UX
 */
export function useOptimisticToast() {
  const { toast, optimisticToasts, isPending } = useToast();
  
  const showOptimisticSuccess = (message: string, asyncFn: () => Promise<any>) => {
    // Show optimistic success toast
    const optimisticId = toast.success(message, { 
      duration: undefined, 
      persistent: true 
    });
    
    // Perform async operation
    return asyncFn()
      .then((result) => {
        // Update to final success state
        toast.update(optimisticId, { 
          persistent: false, 
          duration: TOAST.DURATION.MEDIUM 
        });
        return result;
      })
      .catch((error) => {
        // Replace with error toast
        toast.dismiss(optimisticId);
        toast.handleError(error, 'Operațiunea a eșuat');
        throw error;
      });
  };
  
  const showOptimisticUpdate = (
    optimisticMessage: string,
    asyncFn: () => Promise<any>,
    successMessage?: string,
    errorMessage?: string
  ) => {
    // Show optimistic update toast
    const optimisticId = toast.info(optimisticMessage, { 
      duration: undefined, 
      persistent: true 
    });
    
    return asyncFn()
      .then((result) => {
        toast.update(optimisticId, { 
          message: successMessage || optimisticMessage,
          variant: 'success',
          persistent: false, 
          duration: TOAST.DURATION.MEDIUM 
        });
        return result;
      })
      .catch((error) => {
        toast.update(optimisticId, { 
          message: errorMessage || `${optimisticMessage} - Eșuat`,
          variant: 'error',
          persistent: false, 
          duration: TOAST.DURATION.LONG 
        });
        throw error;
      });
  };
  
  return {
    showOptimisticSuccess,
    showOptimisticUpdate,
    optimisticToasts,
    isPending
  };
} 
